import React, { useState } from "react";

import { Section, StartSection } from "./components/section/Section";
import { Page, GridWrapper } from "./components/section/styles.Section";
import { GlobalStyle } from "./styles.App";
import { Header, LegalLinks } from "./components/header/Header";
import { Menu, MenuButton } from "./components/menu/Menu";
import {
  DeepDive,
  DeepDiveLink,
  DeepDiveCloseButton,
} from "./components/deepdive/DeepDive";
import { AuthorBox, Author } from "./components/author/Author";
import { Quote } from "./components/quote/Quote";
import {
  Anatomy,
  RepresentingKnowledge,
  QualityCriteria,
  Propositions,
  SentenceVectors,
  Evaluation,
  PromptDesign,
  Metadata,
} from "./components/deepdive/content/DeepDiveContent";

//TODO: Weird file imports do not work. Maybe a problem with webpack or the like.
import BgImage from "./assets/distill.png";
import imgRebecca from "./assets/rebecca.jpeg";
import imgMeik from "./assets/meik.jpg";
import imgMarco from "./assets/marcof.jpeg";
import imgPhilipp from "./assets/phillip.jpeg";
import imgCecile from "./assets/cecile.png";
import imgMax from "./assets/max.png";
import GPT3 from "./assets/gpt3.mp4";

import Graph from "./assets/graphExample.svg";

export const table_of_contents = {
  "Climate Context Needed": "#climate-context-needed",
  "Our Goal: Automating Fact Boxes": "#automating-fact-boxes",
  "Creating Quality Fact Boxes": "#creating-quality-fact-boxes",
  "Sharing The Workload": "#sharing-the-workload",
  "The Knowledge Graph": "#knowledge-graph",
  "Matching Articles and Facts": "#matching-articles-and-facts",
  "Full Automation Test": "#full-automation-test",
  "Filling the Gaps": "#filling-the-gaps",
  "GPT-3 and Facts": "#gpt3",
  Synopsis: "#synopsis",
  "Way Forward": "#way-forward",
  "Authors and Credits": "#authors-credits",
};

export default function App() {
  const [currentView, setCurrentView] = useState(true);
  const [currentDive, setCurrentDive] = useState<number | undefined>(undefined);
  const [currentDivePosition, setCurrentDivePosition] = useState("right");
  const [currentDiveBgColor, setCurrentDiveBgColor] = useState("#FFFFFF");
  const [currentBgColor, setCurrentBgColor] = useState("#FFFFFF");

  const [menuVisible, setMenuVisible] = useState(false);
  const [menuId, setMenuId] = useState("#climate-context-needed");
  const [menuColor, setMenuColor] = useState("#FFFFFF");

  const handleClick = () => {
    setMenuVisible(!menuVisible);
  };

  const handleDive = (
    id: number,
    position: string,
    bgColor: string,
    bgColorParent: string,
    backlink?: boolean
  ) => {
    setCurrentView(!currentView);
    setCurrentDive(id);

    if (!backlink) {
      setCurrentDivePosition(position);
      setCurrentDiveBgColor(bgColor);
      setCurrentBgColor(bgColorParent);
    }
  };

  const Dives = ({ id }: { id: number | undefined }) => {
    switch (id) {
      case 2:
        return <QualityCriteria />;
      case 3:
        return <Anatomy />;
      case 4:
        return <Propositions />;
      case 5:
        return <RepresentingKnowledge />;
      case 6:
        return <SentenceVectors />;
      case 7:
        return <Evaluation />;
      case 8:
        return <PromptDesign />;
      case 9:
        return <Metadata />;
      default:
        return <></>;
    }
  };

  return (
    <>
      <GlobalStyle />
      <GridWrapper position={currentDivePosition} isMainView={currentView}>
        <Header>
          <MenuButton menuVisible={menuVisible} onClick={handleClick} />
        </Header>
        <Page>
          <StartSection backgroundColor="#EED0A1" backgroundImage={BgImage} />
          <Section
            backgroundColor="#C4E3E1"
            id={"climate-context-needed"}
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <h2>Climate Context Needed</h2>
            <p>
              Our climate is changing around us, creating a plethora of effects
              around the world. Why would it be bad if the Jet stream weakens?
              And how do emissions from thawing permafrost contribute to global
              warming? There are so many scientific concepts within the field
              that journalists and readers can feel dizzy. In the hurry of daily
              news shifts, there is little time to research and explain all
              these concepts over and over again.
            </p>
            <p>
              There might have been a good article about the Jet stream last
              year, but who knows about it today? The relevant context is hidden
              behind the latest developments in the news cycle, both for
              journalists writing and users reading.
            </p>
            <p>
              This is increasingly relevant as the{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.researchgate.net/publication/344626487_The_Proliferation_of_the_News_Finds_Me_Perception_Across_Societies"
              >
                “News finds me” attitude
              </a>{" "}
              spreads globally. It describes a form of casual news consumption
              especially in users under 35 years of age where users believe that
              relevant news will find them over peers and social networks. In
              Germany, 49 percent of users expressed this belief. Instead of
              seeking out trusted news sources regularly, this group allows
              social streams to shape their overall news consumption. It is easy
              to see why casual news users are an audience underserved with
              context. Our partner team from{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://mediacitybergen.no/"
              >
                {" "}
                Media City Bergen
              </a>{" "}
              sheds additional light on this question with their{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://mediacitybergen.no/home/stimulating-young-peoples-news-consumption-with-facts/"
              >
                own research{" "}
              </a>
              focusing on younger audiences.
            </p>
          </Section>

          <Section
            backgroundColor="#E1DBD2"
            id={"automating-fact-boxes"}
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <h2>Our Goal: Automating Fact Boxes</h2>
            <p>
              To better serve non-avid news readers with relevant context, we
              set out in our 2021 #JournalismAI Collab Challenge to test a
              solution based on modular journalism and artificial intelligence
              (AI). We at Bavarian Broadcasting (BR) and Science Media Center
              Germany want to automatically structure and assemble scientific
              background information about climate change and add it as context
              information to existing climate change news articles.
            </p>
            <p>
              But how can we effectively help readers not feel disconnected from
              recent developments and the daily news stream? Especially those
              audiences who do not follow news daily. And how is it possible to
              provide the necessary context in each news article? To answer
              these questions many online media like BR’s news website BR24.de
              are experimenting with different forms of fact boxes – limited
              text snippets providing essential but condensed background
              information, or as Guardian’s Stephen Pritchard put it:
            </p>

            <Quote>
              [A fact box is] a small device aimed at enticing you into a story
              by presenting a series of bite-sized bits and bobs that should
              both illuminate and embellish an article.
            </Quote>
          </Section>

          <Section
            backgroundColor="#DED2E1"
            id={"creating-quality-fact-boxes"}
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <h2>Creating Quality Fact Boxes</h2>
            <p>
              Fact boxes are hard to write because of their absolute claim to
              truth in a limited space. Each box consists of individual facts
              linked together to provide a complete and ideally unbiased
              overview over a topic. A fact box has to encompass all facts that
              are crucial to depict the scientific evidence on that topic.
            </p>
            <p>
              To automate the writing process a trustable source is needed. The
              Science Media Center (SMC) is a German nonprofit organisation
              specialised in collecting and presenting scientific expertise on
              up-to-date issues for media. The SMC provides dossiers on various
              topics free to use for journalists and with open access on its{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.sciencemediacenter.de/"
              >
                website
              </a>
              . Whenever something happens in science, the SMC delivers curated
              expert statements from different scientific perspectives.
            </p>

            <DeepDiveLink
              direction={"left"}
              id={2}
              onClick={handleDive}
              backgroundColor="#DED2E1"
            >
              Quality Criteria for Fact Boxes
            </DeepDiveLink>
            <p>
              With BR and SMC cooperating in this JournalismAI Collab project,
              we set ourselves the goal to automatically create fact boxes out
              of this scientific content, hoping to provide users with context.
              At the same time, we hoped to offer the fact box writers a
              shortcut through demanding scientific research as well as matching
              between articles and fact boxes, and thereby improve the
              production process.
            </p>
          </Section>
          <Section
            backgroundColor="#C1DAED"
            id={"sharing-the-workload"}
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <h2>Sharing the Workload</h2>
            <p>
              BR24.de has embraced the concept of embeddable fact boxes to
              enhance articles with context regarding the COVID-19 pandemic and
              the German election.
            </p>
            <p>
              Workflow in the production process is a make or break point here.
              We heard from other participants in the Collab that this has been
              a blocker for successful adoption of fact boxes in the newsroom
              practice. Adding extra tasks like creating and updating fact boxes
              onto the already full plate of news editors does not seem like a
              viable route workflow-wise.
            </p>
            <DeepDiveLink
              direction={"right"}
              id={3}
              onClick={handleDive}
              backgroundColor="#C1DAED"
            >
              Anatomy of a Fact Box
            </DeepDiveLink>
            <p>
              BR is currently sharing the workload between different teams: In
              this case, valuable input for the newsroom comes from the BR
              archives with a team of documentalists. They constantly research
              online debates for interesting facts to explain and myths to
              debunk and create the corresponding content themselves. They also
              update existing fact boxes. Our goal is now to provide these teams
              with pre-produced automated fact boxes derived from the scientific
              corpus of Science Media Center.
            </p>
          </Section>

          <Section
            backgroundColor="#EECFDA"
            id="knowledge-graph"
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <h2>The Knowledge Graph</h2>

            <p>
              The SMC has published hundreds of statements on the topic of
              “climate change”. However, this accumulated knowledge is locked
              into weakly structured and loosely connected text artefacts.
            </p>
            <DeepDiveLink
              direction={"right"}
              id={5}
              onClick={handleDive}
              backgroundColor="#EECFDA"
            >
              Representing Knowledge in Graphs
            </DeepDiveLink>
            <p>
              There is no structure that would help a machine to recognise the
              semantics of the information. This complicates the use of
              algorithms to process knowledge contained in the texts, and thus
              the automated use of this knowledge in other contexts.
            </p>

            <p>
              To facilitate this re-use in the future, the SMC is currently
              extracting the knowledge contained in its offerings and
              representing it in the form of a knowledge graph, a database
              structure which emphasises the multitude of possible relations
              between entities.
            </p>
          </Section>
          <Section
            backgroundColor="#EECFDA"
            id={undefined}
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <p>
              The knowledge graph is manually annotated with propositions.
              Propositions abstract particular statements and are very useful
              for bringing scientific knowledge into other contexts like
              journalism.
            </p>
            <DeepDiveLink
              direction={"left"}
              id={4}
              onClick={handleDive}
              backgroundColor="#EECFDA"
            >
              Propositions as Reusable Information
            </DeepDiveLink>

            <p>
              The example in the illustration below shows how two statements
              about the influence of climate change on heavy rainfall events are
              condensed into their common propositional content: A warmer
              atmosphere can absorb more water vapour, which falls as rain
              during a precipitation event. In addition to the propositions, the
              relationship of propositions to statements is also stored in the
              knowledge graph in order to be able to evaluate how well supported
              the content of a proposition is.
            </p>
            <img
              className="noBackground width-eighty content"
              src={Graph}
              alt=""
            />
          </Section>

          <Section
            backgroundColor="#F9EBC5"
            id={"matching-articles-and-facts"}
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <h2>Matching Articles and Facts</h2>
            <p>
              Now that we have organised the scientific content of the SMC into
              a knowledge graph, let’s try to generate a fact box. We have an
              article in need of a fact box and the set of propositions from the
              knowledge graph. To match both, we need to find the proposition
              which comes closest to the topics mentioned in the article.
            </p>
            <p>
              We match propositions and articles by creating vectors which allow
              us to find similarity between propositions in the graph and our
              article – so called sentence embeddings.
            </p>

            <DeepDiveLink
              direction={"left"}
              id={6}
              onClick={handleDive}
              backgroundColor="#F9EBC5"
            >
              Sentence Embeddings
            </DeepDiveLink>

            <p>
              By only using propositions which are a good fit for the article,
              we get a set of propositions to generate a custom fact box for our
              article from the SMC knowledge graph.
            </p>
          </Section>
          <Section
            backgroundColor="#EFCBC4"
            id={"full-automation-test"}
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <h2>Full Automation Test</h2>
            <p>
              To have a functioning workflow, we could also have stopped after
              the matching and querying, offering a selected set of propositions
              with supporting scientific quotes. According to the documentalists
              at BR, this would already be a helpful pre-product for fact box
              creators. An introductory text, propositions and quotes from the
              SMC would form a good basis and could also be enriched with
              further sources by the documentalists.
            </p>
            <p>
              To test the full range of our experimental setup, though, we want
              to try and generate a fully automated custom fact box for our
              article. This means that we will need an AI system to write a fact
              box, using the bits and pieces coming from the knowledge graph as
              input data.
            </p>
            <p>
              Therefore, we will try and see if we can employ state-of-the-art
              language models for this purpose. These systems can help to make a
              coherent shape of our output text-wise and make it ready to
              publish. As one of the more powerful language models available, we
              have chosen GPT-3 by OpenAI for this task.
            </p>
          </Section>
          <Section
            backgroundColor="#CACFD9"
            id={"filling-the-gaps"}
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <h2>Filling the Gaps</h2>
            <p>
              GPT-3 belongs to a family of language models trained on texts from
              the internet. This leads to a kind of inherent knowledge within
              the models. In many cases, the assumptions that these models make
              about the world based on their training data are true.
            </p>

            <DeepDiveLink
              direction={"right"}
              id={8}
              onClick={handleDive}
              backgroundColor="#CACFD9"
            >
              GPT-3 Prompt Design for Journalists
            </DeepDiveLink>

            <p>
              But, there is also a word for language models making wrong
              connections and assumptions in the texts they create. The
              phenomenon is called{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://arxiv.org/pdf/2011.02593.pdf"
              >
                {" "}
                hallucination
              </a>
              . Hallucinations are presented as facts, but cannot be found in
              the input data. In the worst case, they are misleading, or even
              plain wrong. In journalism we need our facts, above all else, to
              be correct, so we must watch out carefully for this phenomenon.
            </p>
            <p>
              To provide as much factual guidance as possible for the model, we
              created a gap-filling-task for German language in GPT-3. By
              prompting GPT-3 to guess how to connect the remaining words from
              these inputs, we automatically generated 20 fact boxes on
              sub-topics of climate change. But can our generated texts live up
              to the rigorous standards that journalists apply to their texts?
            </p>
          </Section>

          <Section
            backgroundColor="#DDE3CF"
            id={"gpt3"}
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <h2>GPT-3 and Facts</h2>
            <p>
              “A robot wrote this entire article. Are you scared yet, human?”
              This disputed essay, that appeared in The Guardian in 2020, was
              meant to demonstrate that GPT-3 may already be close to replacing
              writing professionals like journalists. From the results we
              generated with GPT-3 editing our fact boxes, we do not see this
              happening anytime soon, due to the model’s difficult relationship
              with facts.
            </p>
            <DeepDiveLink
              direction={"right"}
              id={7}
              onClick={handleDive}
              backgroundColor="#DDE3CF"
            >
              Evaluation of Generated Text
            </DeepDiveLink>
            <p>
              We ran a two-fold evaluation of our artificially generated fact
              box tests. In round one done by our team, we saw cases where
              fictional numbers were included, wrongly connected facts, and
              hallucinations. Additionally, we gave two manually tweaked
              generated fact boxes and one entirely artificially generated fact
              box to our fact box writers in the archive and gathered their
              feedback on usability and helpfulness in their editing process.
            </p>
            <p>
              They quickly found several factual issues within the texts. The
              errors raised their mistrust of the generated boxes overall and
              led them to double-check every claim. This was eventually more
              time-consuming than writing up the fact box from the propositions
              set manually.
            </p>
          </Section>
          <Section
            backgroundColor="#DDE3CF"
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <video controls muted className="content">
              <source src={GPT3} type="video/webm" />
            </video>
          </Section>
          <Section
            backgroundColor="#EEDCCC"
            id={"synopsis"}
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <h2>Synopsis</h2>
            <p>
              The initial challenge that we set ourselves was to automatically
              create fact boxes to enrich articles around the topic of climate
              change. The SMC succeeded in organising their vast scientific
              expertise into a machine-readable knowledge graph. We’d like to
              stress that this was not a fully automated process, but needed
              extensive annotation of propositions by hand. Metadata are in fact
              the key to unlock large amounts of data for modular journalism use
              cases.
            </p>

            <DeepDiveLink
              direction={"left"}
              id={9}
              onClick={handleDive}
              backgroundColor="#EEDCCC"
            >
              Joy and Burden of Metadata
            </DeepDiveLink>
            <p>
              This structured knowledge graph was able to provide usable output
              when matched with climate change news articles through the method
              of sentence embedding. We created a good foundation that fact box
              editors would be happy to use as pre-product and research help.
              But in the demanding creation of a fact text from our loosely
              related propositions, AI in the form of the GPT-3 model struggled
              to preserve factual correctness. In our case, the need for
              time-consuming checks canceled out the benefits of increased text
              coherence through the GPT-3 post-processing.
            </p>
          </Section>

          <Section
            backgroundColor="#C0DAC1"
            id={"way-forward"}
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <h2>Way Forward</h2>
            <p>
              For the future of our project, we see a hybrid approach as the
              best solution. One attractive solution, which prioritises workflow
              improvement over full automation, would be the automated creation
              of a ’dossier‘ of propositions including expert statements, that
              could serve as the basis for human-written fact boxes. At BR, we
              will continue to explore this possibility with the archive teams,
              focus on workflow improvements and test for user experience
              benefits.
            </p>
            <p>
              Thinking of an{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.br.de/extra/ai-automation-lab-english/ai-ethics100.html"
              >
                ethical use
              </a>{" "}
              of GPT-3 in journalism, we see a few critical points to watch out
              for at the moment. First of all, it became clear that GPT-3 should
              be used in settings that keep its tendency to hallucinate at bay.
              This includes the general architecture of the prompt, and also
              technical settings, like the creativity threshold. Editorial
              control mechanisms with human journalists checking for factual
              accuracy need to be in place to verify each output.
            </p>
            <p>
              Overall, we see great potential in the modular journalism approach
              to better serve increasingly fragmented audiences, e.g. through
              creating tailored versions of stories. When knowledge is organised
              in a knowledge graph, it is ready to be used and explored in a
              plethora of solutions. The modular journalism approach will in
              fact enable many more interesting use cases for underserved
              audiences, as the work of our fellow #JournalismAI Collab
              Challenges teams highlights.
            </p>
          </Section>

          <Section
            backgroundColor="#ECE5C8"
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
            id={"authors-credits"}
          >
            <AuthorBox>
              <Author
                name="Dr. Meik Bittkowski"
                authorImage={imgMeik}
                twitter="https://twitter.com/jtchd"
              >
                Teamlead Research & Development and Authorized Officer of
                Science Media Center Germany
              </Author>
              <Author
                name="Max Brandl"
                authorImage={imgMax}
                twitter="https://www.twitter.com/maxbrandl"
                linkedin="https://www.linkedin.com/in/maxbrandl"
              >
                Product Designer at BR AI + Automation Lab
              </Author>
              <Author
                name="Rebecca Ciesielski"
                authorImage={imgRebecca}
                twitter="https://twitter.com/r_ciesielski"
              >
                Editor with focus on Algorithmic Accountability at BR AI +
                Automation Lab
              </Author>
              <Author
                name="Phillip Gawlik"
                authorImage={imgPhilipp}
                linkedin="https://de.linkedin.com/in/philipp-gawlik-b230731a8"
              >
                Machine-Learning Engineer at BR AI + Automation Lab
              </Author>
              <Author
                name="Marco Lehner"
                authorImage={imgMarco}
                twitter="https://twitter.com/radadmiral"
              >
                Data-Journalist and Product Developer at BR AI + Automation Lab
              </Author>
              <Author
                name="Cécile Schneider"
                authorImage={imgCecile}
                twitter="https://twitter.com/hellocec"
                linkedin="https://de.linkedin.com/in/cecile-schneider"
              >
                Product Lead at BR AI + Automation Lab
              </Author>
            </AuthorBox>
          </Section>
          <Section
            backgroundColor="#ECE5C8"
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
          >
            <h2>Special Thanks</h2>
            <p>
              Special thanks go to Jane Birch (BBC), Marleen Halbach, Berit
              Reise (SMC), Klaus Weisenbach, Maria-Elisabeth FuchsCon, Carolin
              Freund (BR Archives), Gudrun Riedl (BR24), Steffen Kühne and Uli
              Köppen (BR AI + Automation Lab) for sharing their expertise and
              support.
            </p>
          </Section>

          <Section
            backgroundColor={"#ECE5C8"}
            setMenuId={setMenuId}
            setMenuColor={setMenuColor}
            id={undefined}
          >
            <h2>Credits</h2>

            <p>
              This project is part of the{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://blogs.lse.ac.uk/polis/2021/03/23/journalismai-collab-challenges/"
              >
                {" "}
                2021 JournalismAI Collab Challenges
              </a>
              , a global initiative that brings together media organisations to
              explore innovative solutions to improve journalism via the use of
              AI technologies.
            </p>
            <p>
              It was developed as part of the EMEA cohort of the Collab
              Challenges that focused on “How might we use modular journalism
              and AI to assemble new storytelling formats and reach currently
              underserved audiences?” with the support of{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://bbcnewslabs.co.uk/"
              >
                BBC News Labs
              </a>{" "}
              and{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://clwstwr.org.uk/"
              >
                Clwstwr
              </a>
              .
            </p>
            <p>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.lse.ac.uk/media-and-communications/polis/JournalismAI"
              >
                JournalismAI
              </a>{" "}
              is a project of{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.lse.ac.uk/media-and-communications/polis"
              >
                Polis
              </a>{" "}
              – the journalism think-tank at the London School of Economics and
              Political Science – and it’s sponsored by the{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://newsinitiative.withgoogle.com/"
              >
                Google News Initiative
              </a>
              . If you want to know more about the Collab Challenges and other
              JournalismAI activities, sign up for the{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://mailchi.mp/lse.ac.uk/journalismai"
              >
                newsletter
              </a>{" "}
              or get in touch with the team via{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="mailto:hello@journalismai.info"
              >
                hello@journalismai.info
              </a>
              .
            </p>

            <h2 style={{ marginTop: "1.5rem" }}>Images</h2>

            <ul>
              <li>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.europeana.eu/de/item/9200579/xxfgwnfx"
                >
                  Distillation equipment
                </a>{" "}
                by{" "}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://wellcomecollection.org"
                >
                  Wellcome Collection
                </a>{" "}
                (via{" "}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.europeana.eu"
                >
                  europeana.eu
                </a>
                ), published under a{" "}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://creativecommons.org/licenses/by/4.0/"
                >
                  Creative Commons BY 4.0
                </a>{" "}
                license
              </li>
              <li>
                Wikidata knowledge graph example using SPARQL by{" "}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://commons.wikimedia.org/wiki/User:Fuzheado"
                >
                  Fuzheado
                </a>{" "}
                on{" "}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://commons.wikimedia.org/wiki/File:Wikidata-knowledge-graph-fuzheado-metobjects-2.png"
                >
                  Wikipedia
                </a>
                , published under a{" "}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://creativecommons.org/licenses/by-sa/4.0/"
                >
                  Creative Commons BY-SA 4.0
                </a>{" "}
                license
              </li>
              <li>
                Team pictures with friendly permission of the respective persons
              </li>
              <li>Additional graphics by BR AI + Automation Lab</li>
            </ul>
            <LegalLinks
              isFloating={false}
              className={"imprintMobile"}
              style={{margin: "3rem 0"}}
            />
          </Section>
        </Page>

        {menuVisible && (
          <Menu
            currentAnchor={menuId}
            backgroundColor={menuColor}
            isVisible={menuVisible ? true : false}
            handleClick={menuVisible === undefined ? undefined : handleClick}
          />
        )}

        {currentDive && (
          <DeepDive position={currentDivePosition} preventScroll={currentView}>
            <Header showLogo={false} showLegal={false}>
              <DeepDiveCloseButton
                id={currentDive}
                direction={currentDivePosition === "left" ? "right" : "left"}
                onClick={handleDive}
                backlink={true}
                backgroundColor={currentDiveBgColor}
                backLinkColor={currentBgColor}
              />
            </Header>
            <Section bigMargins={true} backgroundColor={currentDiveBgColor}>
              <Dives id={currentDive}></Dives>
              <DeepDiveLink
                id={currentDive}
                direction={currentDivePosition === "left" ? "right" : "left"}
                onClick={handleDive}
                backlink={true}
                backgroundColor={currentDiveBgColor}
                backLinkColor={currentBgColor}
              >
                Back to main story
              </DeepDiveLink>
            </Section>
          </DeepDive>
        )}
      </GridWrapper>
    </>
  );
}
